import { useEffect, useState, useContext } from "react";
import axios from 'axios';

import { AppContext } from '../../../context.js';
import Helmet from "../../components/helmet.js";
import { Button } from '../../components/component.js';

function currentPerformanceYearStart() {
  const now = new Date();
  const y = now.getUTCFullYear();
  return now.getUTCMonth() >= 6 ? y : y - 1;
}

function RespiteBenefits() {
  const context = useContext(AppContext);
  const firstPerformanceYearStart = 2024;
  const currentPerformanceYear = currentPerformanceYearStart();

  const [loading, setLoading] = useState(true);
  const [caps, setCaps] = useState([]);
  const [savedCaps, setSavedCaps] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const allStartYears = Array.from(
    { length: currentPerformanceYear - firstPerformanceYearStart + 1 },
    (_, k) => firstPerformanceYearStart + k
  );

  function mapCapsFromApi(existing) {
    return existing
      .map((c) => ({ startYear: c.startYear, amount: parseFloat(c.amount).toFixed(2) }))
      .sort((a, b) => a.startYear - b.startYear);
  }

  function expandCaps(configured) {
    const byYear = new Map(configured.map((c) => [c.startYear, c.amount]));
    return allStartYears.map((startYear) => ({
      startYear,
      amount: byYear.has(startYear) ? byYear.get(startYear) : '',
    }));
  }

  useEffect(() => {
    let u = context.addSpiner();
    axios.get('/api/admin/settings').then((response) => {
      const mapped = mapCapsFromApi(response.data.respiteCaps || []);
      setCaps(mapped);
      setSavedCaps(mapped);
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function handleEdit() {
    setCaps(expandCaps(savedCaps));
    setEditing(true);
  }

  function handleCancel() {
    setCaps(savedCaps.map((c) => ({ ...c })));
    setEditing(false);
  }

  function updateAmount(index, value) {
    const updated = caps.map((c, i) => i === index ? { ...c, amount: value } : c);
    setCaps(updated);
  }

  function handleAmountBlur(index, value) {
    if (value === '' || value === null || value === undefined) {
      updateAmount(index, '');
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      updateAmount(index, parsed.toFixed(2));
    }
  }

  function handleSave() {
    const toSave = [];
    for (const cap of caps) {
      const trimmed = typeof cap.amount === 'string' ? cap.amount.trim() : String(cap.amount ?? '');
      if (trimmed === '') {
        continue;
      }
      const amt = parseFloat(trimmed);
      if (isNaN(amt) || amt <= 0) {
        context.addError({ message: `Benefit amount for ${cap.startYear}–${cap.startYear + 1} must be a valid positive number.` });
        return;
      }
      toSave.push({ startYear: cap.startYear, amount: amt.toFixed(2) });
    }

    setSaving(true);
    let u = context.addSpiner();
    axios.post('/api/admin/settings/respiteCaps/update', {
      caps: toSave.map((c) => ({ startYear: c.startYear, amount: parseFloat(c.amount) })),
    }).then(() => {
      setSavedCaps(toSave.map((c) => ({ ...c })));
      setCaps(toSave.map((c) => ({ ...c })));
      setEditing(false);
      context.addNotification && context.addNotification('Respite annual benefit saved.');
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      setSaving(false);
      context.removeSpiner(u);
    });
  }

  if (loading) return null;

  const displayCaps = editing ? caps : expandCaps(savedCaps);

  return (
    <>
      <Helmet title="Settings | Respite Annual Benefit" />

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="typo-hero-small">Respite Annual Benefits</div>
            <div className="typo-normal-175">Configure the org-wide respite benefit per performance year (July 1 – June 30). Transactions within a performance year are measured against that year's amount.</div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="card mb-3">
              <div className="card-header sm">
                <div className="row typo-strong-150">
                  <div className="col-xs-6">Performance Year</div>
                  <div className="col-xs-6">Benefit Amount ($)</div>
                </div>
              </div>

              {displayCaps.map(({ startYear, amount }, i) => (
                <div
                  key={"cap_" + startYear}
                  className={"card-header sm " + (i === displayCaps.length - 1 ? "last" : "")}
                >
                  <div className="row align-items-center">
                    <div className="col-xs-6">
                      <div className="typo-normal-150">{`${startYear}–${startYear + 1}`}</div>
                    </div>
                    <div className="col-xs-6">
                      {editing ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="input"
                          value={amount}
                          onChange={(e) => updateAmount(i, e.target.value)}
                          onBlur={(e) => handleAmountBlur(i, e.target.value)}
                          placeholder="e.g. 2563.00"
                        />
                      ) : (
                        <div className="typo-normal-150" style={amount ? undefined : { color: '#888' }}>
                          {amount || 'not set'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-end align-items-center">
              {editing ? (
                <>
                  <Button onClick={handleCancel} color="grey" text="Cancel" className="me-2" disabled={saving} />
                  <Button onClick={handleSave} color="blue" text={saving ? "Saving…" : "Save"} disabled={saving} />
                </>
              ) : (
                <Button onClick={handleEdit} color="blue" text="Edit" />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RespiteBenefits;
